import { Ban, Calendar, Eye, Play, User } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateWIB } from "@/lib/date-utils";
import { cn } from "@/lib/utils";

import {
  formatSignedRp,
  OPNAME_DATE_FORMAT,
  signedValueClass,
} from "./stock-opname-utils";
import { STOCK_OPNAME_STATUS_LABELS, type StockOpname } from "./types";

export interface StockOpnameRowsProps {
  rows: StockOpname[];
  onOpenOpname: (opname: StockOpname) => void;
  onCancelRequest: (opname: StockOpname) => void;
}

export function StockOpnameDesktopTable({
  rows,
  onOpenOpname,
  onCancelRequest,
}: StockOpnameRowsProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="text-xs">
          <TableHead className="w-[170px]">Kode Sesi</TableHead>
          <TableHead className="w-[120px]">Tanggal</TableHead>
          <TableHead className="w-[140px]">Petugas</TableHead>
          <TableHead className="w-[110px]">Status</TableHead>
          <TableHead className="w-[100px] text-right">Total Barang</TableHead>
          <TableHead className="w-[110px] text-right">Item Selisih</TableHead>
          <TableHead className="w-[150px] text-right">Nilai Selisih</TableHead>
          <TableHead className="w-[160px] text-right">Aksi</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={8}
              className="py-12 text-center text-sm text-muted-foreground"
            >
              Belum ada sesi stock opname yang tercatat.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((opname) => {
            const statusMeta = STOCK_OPNAME_STATUS_LABELS[opname.status];
            const isDraft = opname.status === "draft";

            return (
              <TableRow key={opname.id} className="text-xs">
                <TableCell className="font-medium">
                  <div className="font-mono font-semibold">{opname.code}</div>
                  {opname.note && (
                    <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
                      {opname.note}
                    </p>
                  )}
                  {opname.cancelReason && (
                    <p className="mt-0.5 line-clamp-1 text-[11px] text-destructive">
                      Batal: {opname.cancelReason}
                    </p>
                  )}
                </TableCell>

                <TableCell className="text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {formatDateWIB(opname.date, OPNAME_DATE_FORMAT)}
                    </span>
                  </div>
                </TableCell>

                <TableCell className="text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>{opname.createdByName || "-"}</span>
                  </div>
                </TableCell>

                <TableCell>
                  <Badge
                    variant={statusMeta.variant}
                    className="px-2 py-0.5 text-[10px] capitalize"
                  >
                    {statusMeta.label}
                  </Badge>
                </TableCell>

                <TableCell className="text-right font-mono font-medium">
                  {opname.totalItems}
                </TableCell>

                <TableCell className="text-right">
                  {opname.discrepancyCount > 0 ? (
                    <Badge
                      variant="destructive"
                      className="px-1.5 py-0 font-mono text-[10px]"
                    >
                      {opname.discrepancyCount} item
                    </Badge>
                  ) : (
                    <span className="font-mono text-muted-foreground">0</span>
                  )}
                </TableCell>

                <TableCell
                  className={cn(
                    "text-right font-mono font-semibold",
                    signedValueClass(opname.netDifferenceValue),
                  )}
                >
                  {formatSignedRp(opname.netDifferenceValue)}
                </TableCell>

                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {isDraft ? (
                      <Button
                        size="sm"
                        onClick={() => onOpenOpname(opname)}
                        className="h-7 gap-1 px-2.5 text-xs"
                      >
                        <Play className="h-3 w-3 fill-current" />
                        Lanjut Hitung
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenOpname(opname)}
                        className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Eye className="h-3 w-3" />
                        Detail
                      </Button>
                    )}
                    {opname.status !== "cancelled" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onCancelRequest(opname)}
                        title={
                          isDraft
                            ? "Batalkan Sesi"
                            : "Batalkan & Balikkan Mutasi"
                        }
                        className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
